/**
 * LT-18 Phase 3 — Reusable managed list panel.
 *
 * One component drives all three lists (categories, units, suppliers)
 * because the surface area is identical: add, rename, reorder, archive,
 * restore. Never hard delete (LT-18c review B-d). Suppliers carry extra contact fields
 * (account_ref, contact_name, etc.) — passed in as `extraFields` and
 * rendered inline under the display label when a row is expanded.
 *
 * Props (unchanged from Phase 3 baseline):
 *   title, description, endpoint, labelField, labelPlaceholder,
 *   inputName, createPayload, extraFields, allowReorder, testId
 *
 * LT-18c §4 (2026-09-12) — archive is no longer a one-way door.
 *   • Archived rows now show a prominent "Restore" text button at
 *     the same visual weight as the "Rename" button on active rows.
 *     The archive-icon control is HIDDEN on archived rows — the
 *     Restore button is the un-archive.
 *   • Every row carries `refs_count` (folded into the list GET server-
 *     side), shown as "· N in use". The §4 hard-delete control was
 *     REMOVED in the LT-18c review (B-d, 13/09): archive-never-delete.
 *   • Active rows carry a "Archive — can be restored later" tooltip
 *     on the archive icon so nobody misreads archive as destructive.
 *   • Empty state distinguishes empty from empty-with-archived:
 *     "No active X — N archived. Tick 'Show archived' to see them."
 *   • The 409 "already exists" banner on the add form parses the new
 *     structured detail from the backend and renders an inline
 *     "Restore this one" CTA when the collision is with an archived
 *     row. Cross-panel — categories, units, suppliers all identical.
 *
 * LT-18 §11 bug 12 — every event handler catches. On failure the
 * panel renders an inline message (add form → banner; rename →
 * beside the input; row action → beside the row). NO error path
 * re-throws or lets an unhandled promise reach the render tree.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Plus, Archive, ChevronUp, ChevronDown,
  Check, X, Loader2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { parseApiError, formatApiError } from "../lib/errors";
import { PrimaryButton, QuietButton, Input } from "./UI";

const cls = (...a) => a.filter(Boolean).join(" ");

/**
 * LT-18c inline-field focus-loss fix (2026-09-12).
 *
 * The previous shape held field state on the parent AND PATCHed on every
 * keystroke, then reloaded the whole list — six state updates and a
 * round trip per character, which tore the input DOM node down mid-
 * type and cost focus, cursor position and scroll position. Chris
 * reported it live: typing "MA2320" into Account ref, first character
 * only, cursor gone, page jumped.
 *
 * Same disease as LT-14's SplitPctInput; same cure:
 *   1. Field holds its own string while being typed (local state).
 *   2. Save fires on blur, not on keystroke — plus Enter as a shortcut.
 *   3. External value is reconciled INTO local state ONLY when the
 *      field is not focused (otherwise a race with a pending commit
 *      would trample the user's typing).
 *   4. Parent merges the response into that ONE row in place —
 *      never `load()` on a field save.
 *
 * Declared at module scope. Placing this inside `ManagedListPanel`'s
 * function body would make it a new component type on every render
 * (the classic footgun), remounting the subtree on every parent
 * state change — exactly what we're trying to stop.
 */
function InlineFieldInput({ initialValue, onCommit, name, testId, placeholder }) {
  const [local, setLocal] = React.useState(initialValue || "");
  const focused = React.useRef(false);
  const baseline = React.useRef(initialValue || "");

  React.useEffect(() => {
    baseline.current = initialValue || "";
    if (!focused.current) setLocal(initialValue || "");
  }, [initialValue]);

  const commit = () => {
    const v = local || "";
    if (v !== baseline.current) {
      baseline.current = v;
      onCommit(v);
    }
  };

  return (
    <Input
      value={local}
      onChange={setLocal}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      name={name}
      placeholder={placeholder}
      autoComplete="off"
      data-testid={testId}
    />
  );
}

/* LT-19f — "m3" almost always means m³. Offer the superscript spelling
 * (create + rename) instead of making Chris hunt for Alt+0179. */
const SUPERSCRIPTS = { "2": "²", "3": "³" };
export function superscriptSuggestion(label) {
  const m = /^(.*[a-z])([23])$/i.exec((label || "").trim());
  return m ? `${m[1]}${SUPERSCRIPTS[m[2]]}` : null;
}
function SuperscriptHint({ value, onUse, testId }) {
  const s = superscriptSuggestion(value);
  if (!s) return null;
  return (
    <button type="button" onClick={() => onUse(s)}
      className="mt-1 inline-flex items-center gap-1 text-accent hover:text-ink" style={{ fontSize: 11 }}
      title="Use the small raised figure, as m² does" data-testid={testId}>
      Did you mean <span className="font-mono text-ink" style={{ fontSize: 12 }}>{s}</span>? Use it
    </button>
  );
}

/* LT-19e — one row's aliases: read-only chips, "edit" swaps in a comma-
 * separated input; Enter / blur saves the whole list, Escape cancels. */
function AliasesLine({ item, testId, disabled, onSave }) {
  const [open, setOpen] = React.useState(false);
  const [local, setLocal] = React.useState("");
  const list = item.aliases || [];
  const archived = item.status === "archived";
  if (archived && list.length === 0) return null;
  const start = () => { setLocal(list.join(", ")); setOpen(true); };
  const commit = () => {
    setOpen(false);
    const next = local.split(",").map((s) => s.trim()).filter(Boolean);
    if (next.join("\u0000") !== list.join("\u0000")) onSave(next);
  };
  return (
    <div className="px-4 pb-2 -mt-1 flex items-center gap-2 text-ink-4" style={{ fontSize: 11 }} data-testid={`${testId}-aliases-${item.id}`}>
      {open ? (
        <>
          <span className="shrink-0">Also known as</span>
          <Input value={local} onChange={setLocal} autoFocus autoComplete="off"
            name={`lt-${testId}-aliases-${item.id}`} placeholder="metre, metres, lin m — comma separated"
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setOpen(false); } }}
            data-testid={`${testId}-aliases-input-${item.id}`} />
        </>
      ) : list.length > 0 ? (
        <>
          <span className="shrink-0">Also known as</span>
          {list.map((a) => <span key={a} className="font-mono text-ink-3 px-1 rounded bg-surface-2" data-testid={`${testId}-alias-${item.id}`}>{a}</span>)}
          {!archived && (
            <button type="button" onClick={start} disabled={disabled} className="text-accent hover:text-ink"
              data-testid={`${testId}-aliases-edit-${item.id}`}>edit</button>
          )}
        </>
      ) : (
        <button type="button" onClick={start} disabled={disabled} className="text-ink-4 hover:text-accent"
          title="What supplier files call it (metre, lm …) — the CSV import reads those as this row"
          data-testid={`${testId}-aliases-edit-${item.id}`}>+ also known as</button>
      )}
    </div>
  );
}

export function ManagedListPanel({
  title,
  description,
  endpoint,
  labelField = "label",
  labelPlaceholder,
  inputName,
  createPayload = (values) => ({ [labelField]: values.label }),
  extraFields = [],
  // LT-18c / LT-19b §1 — boolean flags rendered as a checkbox on every
  // row (e.g. Units → `is_divisible`). `[{ name, label, title }]`.
  // Saved through the same PATCH-and-merge path as extraFields.
  rowToggles = [],
  // LT-19e — "Also known as" aliases line per row (units, categories):
  // what supplier files call it; the importer resolves them.
  rowAliases = false,
  // LT-19f — offer "m³" when someone types "m3" (units only)
  superscriptHint = false,
  allowReorder = true,
  testId = "managed-list",
}) {
  // LT-18c §4 — we always fetch with include_archived=true so the panel
  // knows the archived count even when `showArchived` is off. Filtering
  // to what's visible happens client-side.
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // Structured error details (parseApiError) so the add form can render
  // an inline Restore CTA when the collision is with an archived row.
  const [formErrorDetail, setFormErrorDetail] = useState(null);
  const [form, setForm] = useState({ label: "", ...Object.fromEntries(extraFields.map((f) => [f.name, ""])) });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);
  // Inline row-scoped error. `{ id, message }`.
  const [rowError, setRowError] = useState(null);
  // LT-18c review B-d (13/09) — the §4 hard-delete control was REMOVED.
  // Archive-never-delete is a standing rule; Restore + the migration's
  // merge step cover the archived-row trap it existed for.

  const setInlineRowError = (id, message) => setRowError({ id, message });
  const clearRowError = (id) => setRowError((cur) => (cur?.id === id ? null : cur));

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get(endpoint, { params: { include_archived: true } })
      .then((r) => setAllItems(r.data || []))
      .catch((err) => {
        const msg = formatApiError(err, `Failed to load ${title.toLowerCase()}`);
        setLoadError(msg);
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, [endpoint, title]);
  useEffect(load, [load]);

  // Client-side split by status.
  const activeItems = allItems.filter((i) => i.status !== "archived");
  const archivedItems = allItems.filter((i) => i.status === "archived");
  const items = showArchived ? allItems : activeItems;

  const submitAdd = async (e) => {
    e.preventDefault();
    setFormErrorDetail(null);
    if (!form.label.trim()) {
      setFormErrorDetail({ message: `${title.slice(0, -1)} label is required.` });
      return;
    }
    setAdding(true);
    try {
      await api.post(endpoint, createPayload(form));
      toast.success(`${title.slice(0, -1)} added`);
      setForm({ label: "", ...Object.fromEntries(extraFields.map((f) => [f.name, ""])) });
      setAddOpen(false);
      load();
    } catch (err) {
      // LT-18c §4 — parse structured detail so we can render Restore CTA.
      setFormErrorDetail(parseApiError(err, "Couldn't add. Please try again."));
    } finally {
      setAdding(false);
    }
  };

  // LT-18c §4 — Restore action from either the row control OR the
  // inline "Restore this one" CTA in the add-form 409 banner.
  const restoreById = async (id) => {
    try {
      await api.post(`${endpoint}/${id}/activate`);
      toast.success("Restored");
      setFormErrorDetail(null);
      setForm({ label: "", ...Object.fromEntries(extraFields.map((f) => [f.name, ""])) });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Restore failed."));
    }
  };

  const saveRename = async (id) => {
    clearRowError(id);
    if (!editValue.trim()) {
      setInlineRowError(id, "Label is required.");
      return;
    }
    setBusyRowId(id);
    try {
      await api.patch(`${endpoint}/${id}`, { [labelField]: editValue.trim() });
      setEditingId(null);
      load();
    } catch (err) {
      setInlineRowError(id, formatApiError(err, "Rename failed."));
    } finally {
      setBusyRowId(null);
    }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swap = items[idx + dir];
    if (!swap) return;
    clearRowError(item.id);
    setBusyRowId(item.id);
    try {
      await Promise.all([
        api.patch(`${endpoint}/${item.id}`, { order: swap.order }),
        api.patch(`${endpoint}/${swap.id}`, { order: item.order }),
      ]);
      load();
    } catch (err) {
      setInlineRowError(item.id, formatApiError(err, "Reorder failed."));
    } finally {
      setBusyRowId(null);
    }
  };

  const archiveRow = async (item) => {
    clearRowError(item.id);
    setBusyRowId(item.id);
    try {
      await api.post(`${endpoint}/${item.id}/archive`);
      load();
    } catch (err) {
      setInlineRowError(item.id, formatApiError(err, "Archive failed."));
    } finally {
      setBusyRowId(null);
    }
  };

  const restoreRow = async (item) => {
    clearRowError(item.id);
    setBusyRowId(item.id);
    try {
      await api.post(`${endpoint}/${item.id}/activate`);
      load();
    } catch (err) {
      setInlineRowError(item.id, formatApiError(err, "Restore failed."));
    } finally {
      setBusyRowId(null);
    }
  };

  const saveExtraField = async (id, field, value) => {
    clearRowError(id);
    setBusyRowId(id);
    try {
      const resp = await api.patch(`${endpoint}/${id}`, { [field]: value });
      // LT-18c focus-loss fix — merge the response into that one row
      // in place. NEVER call load() on a field save: a full refetch
      // triggers the render storm that tears the input DOM node down
      // mid-type. refs_count is only computed by the list endpoint,
      // so preserve it explicitly.
      setAllItems((cur) => cur.map((r) =>
        r.id === id
          ? { ...r, ...(resp?.data || {}), refs_count: r.refs_count }
          : r
      ));
    } catch (err) {
      setInlineRowError(id, formatApiError(err, "Save failed."));
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <div className="rounded-md border border-line bg-surface" data-testid={testId}>
      <div className="px-4 py-3 border-b border-line flex items-center justify-between">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{title}</h3>
          {description && <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>{description}</p>}
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 11.5 }}>
            <input type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid={`${testId}-show-archived`} />
            Show archived
            {archivedItems.length > 0 && (
              <span className="text-ink-4 font-mono" style={{ fontSize: 10.5 }}>
                ({archivedItems.length})
              </span>
            )}
          </label>
          <QuietButton icon={Plus} onClick={() => { setAddOpen((o) => !o); setFormErrorDetail(null); }}
            data-testid={`${testId}-add-toggle`}>
            Add
          </QuietButton>
        </div>
      </div>

      {loadError && (
        <div
          className="px-4 py-2 border-b border-line-soft bg-note-red/10 text-note-red flex items-start gap-2"
          style={{ fontSize: 12 }}
          data-testid={`${testId}-load-error`}
        >
          <AlertCircle size={13} className="mt-0.5 flex-none" />
          <span>{loadError}</span>
        </div>
      )}

      {addOpen && (
        <form
          onSubmit={submitAdd}
          className="px-4 py-3 border-b border-line-soft bg-surface-2/40"
          autoComplete="off"
        >
          <div className="grid gap-2" style={{ gridTemplateColumns: extraFields.length > 0 ? "1fr 1fr" : "1fr" }}>
            <div>
              <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>
                {labelField === "name" ? "Name" : "Label"}
              </label>
              <Input
                value={form.label}
                onChange={(v) => { setForm((f) => ({ ...f, label: v })); if (formErrorDetail) setFormErrorDetail(null); }}
                placeholder={labelPlaceholder || ""}
                name={inputName || `lt-${testId}-label`}
                autoComplete="off"
                data-testid={`${testId}-add-label`}
              />
              {superscriptHint && (
                <SuperscriptHint value={form.label} testId={`${testId}-add-superscript`}
                  onUse={(s) => { setForm((f) => ({ ...f, label: s })); if (formErrorDetail) setFormErrorDetail(null); }} />
              )}
            </div>
            {extraFields.map((f) => (
              <div key={f.name}>
                <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>{f.label}</label>
                <Input value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
                  placeholder={f.placeholder || ""}
                  name={`lt-${testId}-${f.name}`}
                  autoComplete="off"
                  data-testid={`${testId}-add-${f.name}`} />
              </div>
            ))}
          </div>

          {formErrorDetail && (
            <div
              className="mt-3 flex items-start gap-2 text-note-red"
              style={{ fontSize: 12 }}
              data-testid={`${testId}-add-error`}
            >
              <AlertCircle size={13} className="mt-0.5 flex-none" />
              <div className="flex-1">
                <div>{formErrorDetail.message}</div>
                {/* LT-18c §4 — inline Restore CTA when the collision is
                    with an archived row. `existing_id` is only set by
                    the LT-18c structured 409. */}
                {formErrorDetail.existing_id && formErrorDetail.existing_status === "archived" && (
                  <button
                    type="button"
                    onClick={() => restoreById(formErrorDetail.existing_id)}
                    className="mt-1.5 underline text-ink hover:text-accent"
                    style={{ fontSize: 12 }}
                    data-testid={`${testId}-add-error-restore`}
                  >
                    Restore &ldquo;{formErrorDetail.existing_label}&rdquo; instead
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-end gap-2">
            <QuietButton onClick={() => { setAddOpen(false); setFormErrorDetail(null); setForm({ label: "", ...Object.fromEntries(extraFields.map((f) => [f.name, ""])) }); }}>Cancel</QuietButton>
            <PrimaryButton type="submit" disabled={adding} icon={adding ? Loader2 : Check}
              data-testid={`${testId}-add-submit`}>
              Add
            </PrimaryButton>
          </div>
        </form>
      )}

      {loading ? (
        <div className="px-4 py-6 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="px-4 py-6 text-center text-ink-4 italic"
          style={{ fontSize: 12 }}
          data-testid={`${testId}-empty`}
        >
          {/* LT-18c §4 — distinguish "empty" from "empty-with-archived". */}
          {archivedItems.length > 0 && !showArchived ? (
            <>
              No active {title.toLowerCase()} — {archivedItems.length} archived.
              {" "}
              <button
                type="button"
                onClick={() => setShowArchived(true)}
                className="underline not-italic text-ink-2 hover:text-ink"
                data-testid={`${testId}-empty-show-archived`}
              >
                Show archived
              </button>
              {" "}to see them.
            </>
          ) : (
            <>No {title.toLowerCase()} yet</>
          )}
        </div>
      ) : (
        <div>
          {items.map((item, idx) => {
            const archived = item.status === "archived";
            const editing = editingId === item.id;
            const expanded = expandedId === item.id;
            const thisRowError = rowError?.id === item.id ? rowError.message : null;
            const refsCount = Number(item.refs_count ?? 0);
            return (
              <div key={item.id}
                className={cls(
                  "border-b border-line-soft last:border-b-0",
                  archived ? "bg-surface-2/40" : "",
                )}
                data-testid={`${testId}-row-${item.id}`}>
                <div className="px-4 py-2.5 flex items-center gap-3">
                  {allowReorder && !archived && (
                    <div className="flex flex-col gap-0.5">
                      <button type="button" onClick={() => move(item, -1)}
                        disabled={idx === 0 || busyRowId === item.id}
                        className="text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`${testId}-move-up-${item.id}`}>
                        <ChevronUp size={11} />
                      </button>
                      <button type="button" onClick={() => move(item, 1)}
                        disabled={idx === items.length - 1 || busyRowId === item.id}
                        className="text-ink-3 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`${testId}-move-down-${item.id}`}>
                        <ChevronDown size={11} />
                      </button>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <Input
                            value={editValue}
                            onChange={(v) => { setEditValue(v); if (thisRowError) clearRowError(item.id); }}
                            name={`lt-${testId}-rename-${item.id}`}
                            autoComplete="off"
                            data-testid={`${testId}-rename-input-${item.id}`}
                          />
                          <button type="button" onClick={() => saveRename(item.id)}
                            disabled={busyRowId === item.id}
                            className="text-note-green hover:text-ink"
                            data-testid={`${testId}-rename-save-${item.id}`}>
                            <Check size={14} />
                          </button>
                          <button type="button" onClick={() => { setEditingId(null); clearRowError(item.id); }}
                            className="text-ink-3 hover:text-ink">
                            <X size={14} />
                          </button>
                        </div>
                        {superscriptHint && (
                          <SuperscriptHint value={editValue} testId={`${testId}-rename-superscript-${item.id}`}
                            onUse={(s) => { setEditValue(s); if (thisRowError) clearRowError(item.id); }} />
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => extraFields.length > 0
                          ? setExpandedId(expanded ? null : item.id)
                          : archived
                            ? undefined // no-op on archived rows (Rename is hidden)
                            : (setEditingId(item.id), setEditValue(item[labelField] || ""), clearRowError(item.id))}
                        className="text-left w-full"
                        data-testid={`${testId}-row-label-${item.id}`}
                      >
                        <span className={cls("text-ink", archived && "text-ink-3 line-through")}
                          style={{ fontSize: 13, fontWeight: 500 }}>
                          {item[labelField]}
                        </span>
                        {archived && (
                          <span className="ml-2 inline-flex items-center gap-1 text-ink-3" style={{ fontSize: 10.5 }}>
                            <Archive size={10} /> Archived
                          </span>
                        )}
                        {item.merged_into_key && (
                          <span className="ml-2 text-ink-3" style={{ fontSize: 10.5 }}
                            title="LT-18c §1 — a duplicate label merged by the taxonomy migration. References moved to the row it names; this row is kept, archived, as the record."
                            data-testid={`${testId}-row-merged-${item.id}`}>
                            · merged into <span className="font-mono">{item.merged_into_key}</span>
                          </span>
                        )}
                        <span className="ml-3 font-mono text-ink-4" style={{ fontSize: 10.5 }}>
                          key={item.key}
                        </span>
                        {refsCount > 0 && (
                          <span
                            className="ml-2 text-ink-4"
                            style={{ fontSize: 10.5 }}
                            data-testid={`${testId}-row-refs-${item.id}`}
                            title={`${refsCount} material${refsCount === 1 ? "" : "s"} reference this row`}
                          >
                            · {refsCount} in use
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {rowToggles.map((t) => (
                      <label
                        key={t.name}
                        className={cls("inline-flex items-center gap-1.5 px-2 py-1 cursor-pointer select-none",
                          archived ? "text-ink-4" : "text-ink-3 hover:text-ink")}
                        style={{ fontSize: 11 }}
                        title={t.title || t.label}
                        data-testid={`${testId}-toggle-${t.name}-${item.id}`}
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-[var(--accent,#0F766E)]"
                          checked={!!item[t.name]}
                          disabled={busyRowId === item.id}
                          onChange={(e) => saveExtraField(item.id, t.name, e.target.checked)}
                          data-testid={`${testId}-toggle-input-${t.name}-${item.id}`}
                        />
                        {t.label}
                      </label>
                    ))}
                    {/* LT-18c §4 — Rename ONLY on active, non-editing rows. */}
                    {!editing && !archived && (
                      <button type="button"
                        onClick={() => { setEditingId(item.id); setEditValue(item[labelField] || ""); clearRowError(item.id); }}
                        className="text-ink-3 hover:text-ink px-2 py-1"
                        title="Rename"
                        data-testid={`${testId}-rename-${item.id}`}>
                        <span style={{ fontSize: 11 }}>Rename</span>
                      </button>
                    )}
                    {/* LT-18c §4 — Archive icon ONLY on active rows.
                        Constructive Restore uses a text button; the icon
                        was too subtle to invite a click. */}
                    {!editing && !archived && (
                      <button type="button" onClick={() => archiveRow(item)}
                        disabled={busyRowId === item.id}
                        className="text-ink-3 hover:text-ink px-2 py-1"
                        title="Archive — can be restored later"
                        data-testid={`${testId}-archive-${item.id}`}>
                        <Archive size={13} />
                      </button>
                    )}
                    {archived && (
                      <button type="button" onClick={() => restoreRow(item)}
                        disabled={busyRowId === item.id}
                        className="text-accent hover:text-ink px-2 py-1 font-medium"
                        title="Restore this row"
                        data-testid={`${testId}-restore-${item.id}`}>
                        <span style={{ fontSize: 11.5 }}>Restore</span>
                      </button>
                    )}
                  </div>
                </div>
                {rowAliases && (
                  <AliasesLine item={item} testId={testId} disabled={busyRowId === item.id}
                    onSave={(list) => saveExtraField(item.id, "aliases", list)} />
                )}
                {thisRowError && (
                  <div
                    className="px-4 pb-2 flex items-start gap-2 text-note-red"
                    style={{ fontSize: 12 }}
                    data-testid={`${testId}-row-error-${item.id}`}
                  >
                    <AlertCircle size={13} className="mt-0.5 flex-none" />
                    <span>{thisRowError}</span>
                  </div>
                )}
                {expanded && extraFields.length > 0 && (
                  <div className="px-4 pb-3 pt-1 grid gap-2 bg-surface-2/30" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    {extraFields.map((f) => (
                      <div key={f.name}>
                        <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>{f.label}</label>
                        {/* LT-18c focus-loss fix — see InlineFieldInput at
                            module scope. Local state, save on blur, merge
                            in place. Do NOT swap this back to a bare
                            Input with onChange=save; that was the bug. */}
                        <InlineFieldInput
                          initialValue={item[f.name]}
                          onCommit={(v) => saveExtraField(item.id, f.name, v)}
                          name={`lt-${testId}-${item.id}-${f.name}`}
                          placeholder={f.placeholder || ""}
                          testId={`${testId}-field-${item.id}-${f.name}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
